import { useState, useEffect, useMemo, useRef } from 'react'
import { coreUtils, TransactionSearchField } from '@adc/ui-morpheus'
import type { Maybe, AuctionTransaction } from '@adc/sdk/types'
import logger from '@adc/logger'
import fetch from '@adc/fetch'
import * as graph from '@adc/sdk'
import type { ContextOutput } from '../contextSelector'
import { extractTransactionIdFromUrn } from './utils'

const { gql } = graph

// Extract fragment info at module level (computed once)
const TRANSACTION_SEARCH_FIELD_FRAGMENT_NAME =
  TransactionSearchField[coreUtils.symbols.GRAPH_API_FRAGMENT_REFERENCE].slice(
    3
  )
const TRANSACTION_SEARCH_FIELD_FRAGMENT_DEFINITION =
  TransactionSearchField[coreUtils.symbols.GRAPH_API_FRAGMENT_DEFINITION]

interface UseTransactionDataResult {
  transaction: Maybe<Partial<AuctionTransaction>>
  isLoading: boolean
  error: Error | null
}

/**
 * Fetches full transaction data from a ContextOutput containing transaction sources
 */
export const useTransactionData = (
  initialContext: ContextOutput | null
): UseTransactionDataResult => {
  const [transaction, setTransaction] =
    useState<Maybe<Partial<AuctionTransaction>>>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const previousTxIdRef = useRef<string | null>(null)

  // Find the transaction URN from sources
  const transactionUrn = useMemo(() => {
    if (!initialContext?.sources) return null
    return (
      initialContext.sources.find(s => s.startsWith('adc:transaction:')) ?? null
    )
  }, [initialContext?.sources])

  const transactionId = useMemo(() => {
    return transactionUrn ? extractTransactionIdFromUrn(transactionUrn) : null
  }, [transactionUrn])

  useEffect(() => {
    if (
      transactionId !== null &&
      previousTxIdRef.current !== null &&
      transactionId !== previousTxIdRef.current
    ) {
      setTransaction(null)
    }

    previousTxIdRef.current = transactionId

    if (!transactionId || !transactionUrn) {
      return
    }

    const fetchTransaction = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetch.forAuctionGraph<
          'seek',
          { total_count: number; content: Partial<AuctionTransaction>[] }
        >({
          query: gql`
            query contextSelector_fetchTransaction($dsl: String!) {
              seek(dsl: $dsl, type: AUCTION_TRANSACTIONS) {
                total_count
                content {
                  ...${TRANSACTION_SEARCH_FIELD_FRAGMENT_NAME}
                }
              }
            }
            ${TRANSACTION_SEARCH_FIELD_FRAGMENT_DEFINITION}
          `,
          variables: {
            dsl: JSON.stringify({
              query: {
                equals: {
                  fields: 'transaction_urn',
                  values: [transactionUrn],
                },
              },
            }),
          },
        })

        const content = response?.body?.data?.seek?.content || []
        const fetchedTransaction = content[0] ?? null

        if (fetchedTransaction) {
          setTransaction(fetchedTransaction)
        } else {
          logger.warn(
            `Transaction ${transactionId} not found, using minimal object`
          )
          setTransaction({
            transaction_urn: transactionUrn,
          } as Partial<AuctionTransaction>)
        }
      } catch (err) {
        logger.error('Failed to fetch transaction data:', err)
        setError(err as Error)
        setTransaction({
          transaction_urn: transactionUrn,
        } as Partial<AuctionTransaction>)
      } finally {
        setIsLoading(false)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchTransaction()
  }, [transactionId, transactionUrn])

  return { transaction, isLoading, error }
}
